import React, { useState } from 'react';
import {
  Stack,
  Button,
  Card,
  Box,
  useTheme,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import moment from 'moment';
import { DatePicker } from '@mui/lab';
import { IStudentTransaction, TransactionFilters } from 'src/types/transaction.types';
import { TransactionsTable } from 'src/components/transactions/TransactionsTable';
import { transactionService } from 'src/services/transaction.service';
import { TransactionModal } from 'src/components';
import palette from 'src/theme/palette';
import { CiSearch } from 'react-icons/ci';
import { Select } from 'src/components';
// import { studentService } from 'src/services/student.service';
// import palette from 'src/theme/palette';

const ROWS_PER_PAGE = 10;

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  minWidth: 160,
  '& .MuiOutlinedInput-root': {
    height: 48,
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      backgroundColor: theme.palette.grey[300],
    },
    '& fieldset': {
      borderWidth: 0,
    },
  },
}));

export function Transactions() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { studentId } = useParams();
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(ROWS_PER_PAGE);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);

  const [filters, setFilters] = React.useState<TransactionFilters>(() => {
    return studentId ? { studentId, search: '' } : { search: '' };
  });

  // Update filters when studentId param changes
  React.useEffect(() => {
    if (studentId) {
      setFilters((prev) => ({ ...prev, studentId }));
    }
  }, [studentId]);

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [transactionToDelete, setTransactionToDelete] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (location.state) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const handleFilterChange = (key: keyof TransactionFilters, value: any) => {
    const newFilters = {
      ...filters,
      [key]: value === '' ? undefined : value,
    };
    setFilters(newFilters);
    setPage(1);
  };

  // Debounce filter changes
  React.useEffect(() => {
    const timer = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: ['transactions'],
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleDeleteClick = (transaction: IStudentTransaction) => {
    setIsDeleteModalOpen(true);
    setTransactionToDelete(transaction.id);
  };

  const queryClient = useQueryClient();

  const handleConfirmDelete = async () => {
    if (transactionToDelete) {
      try {
        await transactionService.deleteTransaction(transactionToDelete);
        setTransactionToDelete(null);
        setIsDeleteModalOpen(false);
        refetchTransactions();
      } catch (error) {
        console.error('Failed to delete transaction:', error);
      }
    }
  };

  const refetchTransactions = () => {
    queryClient.invalidateQueries({
      queryKey: ['transactions', { page, rowsPerPage, filters }],
    });
  };

  const handleCloseDeleteModal = () => {
    setTransactionToDelete(null);
    setIsDeleteModalOpen(false);
  };

  // Fetch transactions
  const { data: transactionsData, isLoading } = useQuery({
    queryKey: ['transactions', { page, rowsPerPage, filters }],
    queryFn: () => transactionService.getTransactions(page, rowsPerPage, filters),
  });

  // Fetch transaction types for filter
  const { data: transactionTypes } = useQuery({
    queryKey: ['transactionTypes'],
    queryFn: () => transactionService.getTransactionTypes(1, 100),
  });

  // Fetch students for filter
  const { data: students } = useQuery({
    queryKey: ['students'],
    queryFn: () => transactionService.getActiveStudents(),
  });

  return (
    // <Container maxWidth="xl">
    <Card
      style={{
        borderRadius: 16,
        marginTop: 30,
        boxShadow: 'none',
      }}
    >
      <Stack spacing={2} m={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <div
              className="_login_row search_container"
              style={{
                backgroundColor: 'var(--grey-200)',
                borderRadius: 12,
              }}
            >
              <CiSearch size={24} color={palette.text.primary} />
              <input
                type="text"
                placeholder="Search by Student Name"
                className="_auth_fields searchField"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                // onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Select
              value={filters.type || ''}
              onChange={(value) => handleFilterChange('type', value)}
              options={[
                { label: 'Credit', value: 'credit' },
                { label: 'Debit', value: 'debit' },
              ]}
              placeholder="Type"
              firstItem="All Types"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
              <TextField
                type="date"
                size="small"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={startDate ? moment(startDate).format('YYYY-MM-DD') : ''}
                onChange={(e) => {
                  const val = e.target.value ? new Date(e.target.value) : null;
                  setStartDate(val);
                }}
                sx={{
                  flex: 1,
                  bgcolor: 'var(--grey-200)',
                  borderRadius: 1.5,
                  '& .MuiOutlinedInput-root': { height: 50 },
                }}
              />
              <TextField
                type="date"
                size="small"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={endDate ? moment(endDate).format('YYYY-MM-DD') : ''}
                onChange={(e) => {
                  const val = e.target.value ? new Date(e.target.value) : null;
                  setEndDate(val);
                }}
                sx={{
                  flex: 1,
                  bgcolor: 'var(--grey-200)',
                  borderRadius: 1.5,
                  '& .MuiOutlinedInput-root': { height: 50 },
                }}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={2}>
            <Button
              variant="contained"
              startIcon={<Icon icon="eva:plus-fill" width={20} height={20} />}
              onClick={handleOpenModal}
              sx={{
                height: 56,
                borderRadius: 1.5,
              }}
              fullWidth
            >
              New Transaction
            </Button>
          </Grid>
        </Grid>
        {/* <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}> */}
        {/* <StyledSearch
            placeholder="Search transactions..."
            style={{ flex: 1 }}
            value={filters.search || ''}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="eva:search-fill" width={24} height={24} />
                </InputAdornment>
              ),
            }}
          /> */}
        {/* <StyledSelect
            select
            value={filters.studentId || ''}
            onChange={(e) => handleFilterChange('studentId', e.target.value)}
            // sx={{ flex: 1 }}
            // label="SelectStudent"
          >
            <MenuItem value="">All Students</MenuItem>
            {students?.map((student) => (
              <MenuItem key={student.id} value={student.id}>
                {`${student?.name} `}
              </MenuItem>
            ))}
          </StyledSelect> */}
        {/* <StyledSelect
            select
            value={filters.type || ''}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            sx={{ flex: 1 }}
            label="Transaction Type"
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="credit">Credit</MenuItem>
            <MenuItem value="debit">Debit</MenuItem>
          </StyledSelect> */}
        {/* <StyledSelect
            select
            value={filters.transactionTypeId || ''}
            onChange={(e) => handleFilterChange('transactionTypeId', e.target.value)}
            sx={{ flex: 1 }}
            placeholder="Transaction Type"
            label="Transaction Category"
          >
            <MenuItem value="">All Categories</MenuItem>
            {transactionTypes?.data.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </StyledSelect> */}
        {/* <StyledDatePicker
            label="Start Date"
            value={filters.startDate || null}
            onChange={(date) => handleFilterChange('startDate', date)}
            slotProps={{ textField: { sx: { flex: 1 } } }}
          />
          <StyledDatePicker
            label="End Date"
            value={filters.endDate || null}
            onChange={(date) => handleFilterChange('endDate', date)}
            slotProps={{ textField: { sx: { flex: 1 } } }}
          /> */}

        {/* </Box> */}

        {/* Transactions Table */}

        <Box sx={{ minWidth: 800 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TransactionsTable
              data={transactionsData?.data || []}
              total={transactionsData?.total || 0}
              page={page}
              onDelete={handleDeleteClick}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}
        </Box>
      </Stack>

      <TransactionModal open={isModalOpen} closeModal={handleCloseModal} />

      <Dialog open={isDeleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>Delete Transaction</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this transaction?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
    // </Container>
  );
}
